/**
 * Mobile nav toggle — expects a header toggle + primary menu panel.
 *
 * Toggle: [data-nav-toggle] or .site-header__toggle
 * Menu:   [data-nav-menu], #site-navigation, or .site-header__menu
 *
 * Note: WordPress may strip custom data-* attrs from Group blocks in
 * template parts, so ID/class fallbacks are required.
 *
 * --site-header-height is declared in CSS and measured here so the mobile
 * panel sits under the header even when the toggle markup is missing.
 */

function getHeader() {
	return document.querySelector( '.site-header' ) || document.querySelector( 'header' );
}

function syncHeaderHeight() {
	const header = getHeader();
	if ( ! header ) {
		return;
	}

	const bottom = Math.max( 0, Math.ceil( header.getBoundingClientRect().bottom ) );
	document.documentElement.style.setProperty( '--site-header-height', `${ bottom }px` );
}

export function initNavigation() {
	syncHeaderHeight();
	window.addEventListener( 'resize', syncHeaderHeight );
	window.addEventListener( 'load', syncHeaderHeight );

	const header = getHeader();
	if ( header && typeof ResizeObserver !== 'undefined' ) {
		new ResizeObserver( syncHeaderHeight ).observe( header );
	}

	const toggle =
		document.querySelector( '[data-nav-toggle]' ) ||
		document.querySelector( '.site-header__toggle' );
	const menu =
		document.querySelector( '[data-nav-menu]' ) ||
		document.querySelector( '#site-navigation' ) ||
		document.querySelector( '.site-header__menu' );
	const overlay = document.querySelector( '[data-nav-overlay]' );

	if ( ! toggle || ! menu ) {
		return;
	}

	const mobileNavQuery = window.matchMedia( '(max-width: 1023px)' );

	const setOpen = ( open ) => {
		const isMobileNav = mobileNavQuery.matches;

		if ( open && isMobileNav ) {
			syncHeaderHeight();
		}

		menu.classList.toggle( 'is-open', open );
		toggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
		document.documentElement.classList.toggle( 'nav-open', open );

		if ( isMobileNav ) {
			menu.toggleAttribute( 'inert', ! open );
			menu.setAttribute( 'aria-hidden', open ? 'false' : 'true' );
		} else {
			menu.removeAttribute( 'inert' );
			menu.removeAttribute( 'aria-hidden' );
		}

		if ( overlay ) {
			overlay.hidden = ! open;
			overlay.classList.toggle( 'is-visible', open );
		}
	};

	window.addEventListener( 'resize', () => {
		if ( ! mobileNavQuery.matches ) {
			setOpen( false );
		}
	} );

	if ( mobileNavQuery.matches ) {
		setOpen( false );
	}

	toggle.addEventListener( 'click', ( e ) => {
		e.preventDefault();
		setOpen( ! menu.classList.contains( 'is-open' ) );
	} );

	overlay?.addEventListener( 'click', () => setOpen( false ) );

	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key === 'Escape' ) {
			setOpen( false );
		}
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initNavigation );
} else {
	initNavigation();
}
